import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useGameSocket } from "../lib/useGameSocket";
import { OptionButton } from "../components/OptionButton";
import { StageTimer } from "../components/StageTimer";
import { EqBars } from "../components/EqBars";
import { StageProgress, ScoreToast } from "../components/StageProgress";
import { PersonalResult } from "../components/PersonalResult";

export default function StudentPlay() {
  const { code } = useParams();
  const { state, connected } = useGameSocket(code);
  const playerId = localStorage.getItem(`ssd_player_${code}`);
  const myName = localStorage.getItem(`ssd_name_${code}`);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [prevQ, setPrevQ] = useState(null);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [prevScore, setPrevScore] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [wasDisconnected, setWasDisconnected] = useState(false);
  const [reconnectToast, setReconnectToast] = useState(false);

  const q = state?.current_question;

  // Track disconnect / reconnect events for non-alarming banner
  useEffect(() => {
    if (!connected && state) setWasDisconnected(true);
    if (connected && wasDisconnected) {
      setReconnectToast(true);
      setWasDisconnected(false);
      const t = setTimeout(() => setReconnectToast(false), 2500);
      return () => clearTimeout(t);
    }
  }, [connected, state, wasDisconnected]);

  // score-change toast when player's score increments during transitions
  useEffect(() => {
    if (!state?.players || !playerId) return;
    const me = state.players.find((p) => p.id === playerId);
    if (!me) return;
    const delta = (me.score || 0) - prevScore;
    if (delta > 0 && prevScore > 0) {
      // decompose delta into base+speed+bonus visualization heuristically
      const id = Date.now();
      const list = [];
      if (delta >= 250 && delta % 250 === 0 && delta === 250) {
        list.push({ id, points: 250, label: "DEYİM BONUSU", type: "bonus" });
      } else {
        const base = 1000;
        if (delta >= base) {
          list.push({ id, points: base, label: "DOĞRU", type: "base" });
          const rest = delta - base;
          if (rest > 0) list.push({ id: id + 1, points: rest, label: "HIZ BONUSU", type: "speed" });
        }
      }
      if (list.length) {
        setToasts(list);
        setTimeout(() => setToasts([]), 1600);
      }
    }
    setPrevScore(me.score || 0);
  }, [state?.players, playerId, prevScore]);

  useEffect(() => {
    if (q?.id && q.id !== prevQ) {
      setSelected(null); setLocked(false); setPrevQ(q.id);
      // On question change (including reconnect), ask backend whether we already answered
      if (playerId) {
        api.get(`/games/${code}/players/${playerId}/current`).then((r) => {
          if (r.data?.question_id === q.id && r.data?.answered) {
            setSelected(r.data.selected); setLocked(true);
          }
        }).catch(() => {});
      }
    }
  }, [q?.id, prevQ, playerId, code]);

  // Fetch personal summary when game finishes
  useEffect(() => {
    if (state?.status === "FINISHED" && playerId && !summary) {
      api.get(`/games/${code}/players/${playerId}/summary`).then((r) => setSummary(r.data)).catch(() => {});
    }
  }, [state?.status, playerId, code, summary]);

  const submit = async (letter) => {
    if (locked || !q?.id) return;
    setSelected(letter); setLocked(true); setError(null);
    try {
      await api.post(`/games/${code}/answer`, { question_id: q.id, selected: letter }, { params: { player_id: playerId } });
    } catch (e) { setError(e?.response?.data?.detail || "Cevap gönderilemedi"); }
  };

  const myPlayer = useMemo(() => state?.players?.find((p) => p.id === playerId), [state, playerId]);
  const myRank = useMemo(() => {
    if (!state?.players) return null;
    const idx = state.players.findIndex((p) => p.id === playerId);
    if (idx < 0) return null;
    const me = state.players[idx];
    const topFifth = state.players[4];
    const pointsToTop5 = topFifth && idx >= 5 ? Math.max(1, topFifth.score - me.score + 1) : 0;
    return { rank: idx + 1, score: me.score, pointsToTop5, inTop5: idx < 5 };
  }, [state, playerId]);

  if (!state) return <div className="min-h-screen dark bg-[#070B14] text-white flex items-center justify-center">Bağlanıyor…</div>;

  const st = state.status;
  const isResult = st.endsWith("_RESULT");

  // Personal result card takes over at FINISHED
  if (st === "FINISHED") {
    return <PersonalResult code={code} playerId={playerId} name={myName} summary={summary} />;
  }

  return (
    <div className="min-h-screen dark bg-[#070B14] text-white">
      {/* Reconnect banner (non-blocking) */}
      {!connected && wasDisconnected && (
        <div className="fixed top-0 inset-x-0 z-40 bg-amber-500/95 text-black text-center text-xs py-1.5 font-heading" data-testid="reconnect-banner">
          Bağlantı kesildi… Yeniden bağlanılıyor.
        </div>
      )}
      {reconnectToast && (
        <div className="fixed top-0 inset-x-0 z-40 bg-emerald-500/95 text-black text-center text-xs py-1.5 font-heading" data-testid="reconnected-banner">
          ✓ Oyuna yeniden bağlandınız.
        </div>
      )}
      <div className="px-5 py-4 flex items-center justify-between text-xs text-zinc-500">
        <div>Kod <span className="text-white font-heading tracking-widest">{code}</span></div>
        <div className="truncate max-w-[45%] text-zinc-300 font-heading">{myName}</div>
        <div className="tabular-nums">Puan <span className="text-white">{myRank?.score ?? 0}</span></div>
      </div>

      <div className="px-5 pb-24">
        {st === "LOBBY" && (
          <div className="mt-16 text-center">
            <div className="text-5xl">🎉</div>
            <h1 className="mt-4 font-heading text-3xl font-bold">Harika! Oyundasın.</h1>
            <p className="mt-2 text-zinc-400 font-literary italic">Öğretmenin oyunu başlatmasını bekle.</p>
            <div className="mt-10 flex justify-center"><EqBars playing /></div>
          </div>
        )}

        {st === "LISTENING" && (
          <div className="mt-12 text-center">
            {(state.song_index ?? 0) > 0 && (
              <div className="text-xs font-heading tracking-widest text-violet-300 mb-2">SIRADAKİ ŞARKI</div>
            )}
            <div className="text-5xl">🎧</div>
            {state.current_song && (
              <>
                <div className="mt-4 text-zinc-400 font-heading text-xs tracking-widest">{state.current_song.artist?.toUpperCase()}</div>
                <div className="font-literary italic text-3xl text-cyan-200">{state.current_song.song}</div>
                <div className="mt-2 text-zinc-500 text-xs">{state.current_song.total_idioms} deyim · {state.current_song.questions_total} soru</div>
              </>
            )}
            <h1 className="mt-5 font-heading text-2xl font-bold">Şarkıyı dikkatle dinle</h1>
            <p className="mt-2 text-zinc-400 text-sm px-4">Şarkı öğretmen ekranından çalıyor. Duyduğun deyimleri hatırlamaya çalış!</p>
            <div className="mt-8 flex justify-center"><EqBars playing /></div>
            <div className="mt-3 text-zinc-600 text-xs">Ses öğretmeninin cihazında çalar. Telefonunda ses yok.</div>
          </div>
        )}

        {st === "COUNTDOWN" && (
          <div className="mt-32 text-center">
            <div className="font-heading font-bold tabular-nums text-8xl bg-gradient-to-br from-cyan-300 to-violet-400 bg-clip-text text-transparent" data-testid="student-countdown">
              <StageTimer deadline={state.stage_deadline}/>
            </div>
            <div className="mt-4 text-zinc-400 font-literary italic">Hazır ol…</div>
          </div>
        )}

        {(st.endsWith("_QUESTION") || st.endsWith("_RESULT")) && q && (
          <div className="mt-4">
            {state.current_song && (
              <div className="mb-3 text-center">
                <div className="text-[10px] font-heading tracking-widest text-cyan-300/90">
                  {state.current_song.artist.toUpperCase()} · <span className="text-cyan-200">{state.current_song.song}</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">Deyim {state.current_song.position} / {state.current_song.total_idioms}</div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-heading tracking-widest text-cyan-300">
                {q.stage === "idiom" ? "🎧 DEYİMİ YAKALA" : q.stage === "meaning" ? "💭 ANLAMINI YAKALA" : "🧠 BAĞLAMI YAKALA"}
              </div>
              {st.endsWith("_QUESTION") && <StageTimer deadline={state.stage_deadline} className="text-lg"/>}
            </div>
            <div className="mt-3 rounded-2xl border border-white/10 bg-zinc-950/70 p-4" translate="no">
              <p className="font-literary text-lg leading-snug notranslate">{q.text}</p>
            </div>
            <div className="mt-4 space-y-3" translate="no">
              {q.options.map((opt, i) => {
                const L = "ABCDE"[i];
                return (
                  <OptionButton
                    key={L} letter={L}
                    onClick={() => submit(L)}
                    disabled={locked || st.endsWith("_RESULT")}
                    selected={selected === L}
                    correct={isResult && q.correct === L}
                    showResult={isResult}
                    testId={`opt-${L}`}
                  ><span className="notranslate">{opt}</span></OptionButton>
                );
              })}
            </div>
            {locked && !isResult && (
              <div translate="no" className="notranslate mt-4 text-center text-emerald-300 font-heading text-sm" data-testid="locked-msg">
                ✓ Kaydedildi
              </div>
            )}
            {isResult && q.correct && (
              <div translate="no" className={`notranslate mt-4 text-center font-heading text-lg ${selected === q.correct ? "text-emerald-300" : "text-rose-200"}`}>
                {selected === q.correct ? "✓ DOĞRU!" : "✕ Bu kez olmadı"}
              </div>
            )}
            {error && <div className="mt-3 text-red-300 text-sm text-center">{error}</div>}
            <div className="mt-6 flex justify-center">
              <StageProgress current={q.stage} isResult={isResult} />
            </div>
          </div>
        )}

        {/* Floating score toast (non-blocking, brief) */}
        <ScoreToast items={toasts} />

        {(st === "LEADERBOARD" || st === "IDIOM_LEADERBOARD") && (
          <div className="mt-8 text-center" data-testid={st === "IDIOM_LEADERBOARD" ? "student-mini-lb" : "student-song-lb"}>
            {st === "IDIOM_LEADERBOARD" ? (
              <>
                <div className="text-4xl">🏆</div>
                <div className="mt-2 text-xs font-heading tracking-widest text-cyan-300">DEYİM TAMAMLANDI</div>
                <h1 className="mt-1 font-heading text-2xl font-bold">Anlık Liderlik</h1>
              </>
            ) : (
              <>
                <div className="text-4xl">🎵</div>
                <div className="mt-2 text-xs font-heading tracking-widest text-violet-300">ŞARKI TAMAMLANDI</div>
                {state.current_song && (
                  <h1 className="mt-1 font-heading text-2xl font-bold">{state.current_song.song}</h1>
                )}
                <div className="mt-1 text-xs text-cyan-300 font-heading tracking-widest">ŞARKI SONU LİDERLİK</div>
              </>
            )}

            <div className="mt-6 rounded-2xl border border-white/10 bg-zinc-950/70 p-4 text-left">
              {state.top5.map((p, i) => {
                const isMe = myPlayer && p.name === myPlayer.name;
                const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i+1}.`;
                return (
                  <div key={i} className={`flex items-center justify-between py-2 border-b border-white/5 last:border-0 ${isMe ? "text-cyan-200" : ""}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-8 font-heading">{medal}</span>
                      <span className="truncate max-w-[180px]">{p.name}{isMe && " (sen)"}</span>
                    </div>
                    <div className="font-heading tabular-nums">{(p.score || 0).toLocaleString("tr-TR")}</div>
                  </div>
                );
              })}
            </div>

            {myRank && (
              <div className="mt-5 rounded-2xl border border-cyan-400/30 bg-cyan-500/5 p-4 text-sm text-cyan-300 space-y-1" data-testid="my-rank">
                {myRank.rank === 1 ? (
                  <>
                    <div className="text-lg">🏆 <span className="text-white font-heading">Şu anda lider sensin!</span></div>
                    <div className="text-zinc-300">{myRank.score.toLocaleString("tr-TR")} puan</div>
                  </>
                ) : myRank.inTop5 ? (
                  <>
                    <div className="text-lg">🔥 <span className="text-white font-heading">Şu anda {myRank.rank}. sıradasın!</span></div>
                    <div className="text-zinc-300">{myRank.score.toLocaleString("tr-TR")} puan</div>
                  </>
                ) : (
                  <>
                    <div>🏆 Şu anda <span className="text-white font-heading">{myRank.rank}. sıradasın</span></div>
                    <div className="text-zinc-300">{myRank.score.toLocaleString("tr-TR")} puanın var</div>
                    <div className="text-zinc-400">İlk 5'e <span className="text-white font-heading">{myRank.pointsToTop5.toLocaleString("tr-TR")}</span> puan kaldı</div>
                  </>
                )}
              </div>
            )}

            {st === "LEADERBOARD" && state.next_song && !state.is_last_song && (
              <div className="mt-5 rounded-2xl border border-violet-400/25 bg-violet-500/5 p-4">
                <div className="text-[10px] font-heading tracking-widest text-violet-300">SIRADAKİ ŞARKI</div>
                <div className="mt-1 font-literary italic text-lg text-violet-200">{state.next_song.song}</div>
                <div className="text-xs text-zinc-500 mt-1">Öğretmen başlattığında yeni şarkı çalacak.</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
